import { easeOut } from 'ol/easing';
import { unByKey } from 'ol/Observable.js';
import assert from 'assert';
import { makeMap, noop } from '@/utils/utils';

export default class GhostAnimator {
  constructor({ ownStyle, olMap, feature, duration, highest, lowest }) {
    this.delayTimer = null; // delayed timer
    this.listenerKey = null; // ol map event hook return key
    this.start = null; // start timestamp
    this.actionEnum = {};
    this.usable = true;

    this.ownStyle = ownStyle ? makeMap(ownStyle) : noop;
    this.lowest = lowest || 1; // lowest  default 1
    this.highest = highest || 1.2; // highest default 1.2
    this.feature = feature; // ol feature
    this.olMap = olMap; // ol map
    this.duration = duration; // animation time
    this.actionType = 'up'; // up or down , default up

    this.init();
  }

  init() {
    this.actionEnum[(this.actionEnum['up'] = 'down')] = 'up';
    this.restart();
  }

  /**
   * 重置feature拥有的style
   * @param {'icon,text'} style
   */
  resetOwnStyle(style) {
    assert(style, 'Reset owned style. Parameters are mandatory');
    this.reset();
    this.delayExecuter();
    this.ownStyle = makeMap(style);
  }

  /** 延时执行 */
  executor() {
    return this.verifyRequirements().then(() => {
      this.listenerKey = this.olMap.on('postcompose', this.animate.bind(this));
      this.render();
    });
  }

  /** 延时执行 */
  delayExecuter() {
    this.clearDelayTimer();
    this.disable();
    this.delayTimer = setTimeout(() => {
      this.clearDelayTimer();
      this.restart();
      this.enable();
    }, 500);
  }

  /** 清除延时定时器 */
  clearDelayTimer() {
    if (!this.delayTimer) return;
    clearTimeout(this.delayTimer);
    this.delayTimer = null;
  }

  /** 重置start */
  restart() {
    this.start = Date.now();
  }

  /** 重置map 和 feature */
  resetMapFeat(map, feat) {
    this.olMap = map;
    this.feature = feat;
    this.delayExecuter();
  }

  /** 校验必须参数 */
  verifyRequirements() {
    return new Promise((resolve, reject) => {
      const { olMap: map, feature: feat } = this;
      if (map && feat) {
        if (!this.usable) resolve('delayed executer');
        else resolve('Execute successfully');
      } else {
        reject(new Error('map or feature is undefined'));
      }
    });
  }

  /** 动画函数 */
  animate(event) {
    if (!this.usable) return;
    const elapsed = event.frameState.time - this.start;
    this.setStyle(this.getStyle(), this.calcY(elapsed));
    this.switchAction(elapsed > this.duration / 2);
    this.render();
  }

  setStyle(style, y, tY) {
    assert(style, 'Style is undefined');
    this.ownStyle('icon') && style.getImage().setAnchor([0.5, y]);
    this.ownStyle('text') && style.getText().setOffsetY(tY || -(y * 30 + 8));
    this.feature.setStyle(style);
  }

  getStyle() {
    const { feature } = this;
    assert(feature, 'Feature is undefined');
    assert(feature.getStyle(), 'This feature has no style');
    if (typeof feature.getStyle() === 'function') {
      const style = feature.getStyle()(feature);
      return style.length ? style[0].clone() : undefined;
    } else {
      return feature.getStyle().clone();
    }
  }

  resetStyle() {
    if (this.feature) this.setStyle(this.getStyle(), 1, -35);
  }

  calcY(elapsed) {
    const duration = this.duration / 2;
    if (this.actionType === 'down') elapsed = duration - elapsed;
    // The lowest is (this.lowest) and the highest is (this.highest)
    return easeOut(elapsed / duration) * (this.highest - this.lowest) + this.lowest;
  }

  switchAction(judge) {
    if (judge) {
      this.actionType = this.actionEnum[this.actionType];
      this.restart();
    }
  }

  // tell OpenLayers to continue postcompose animation
  render() {
    this.olMap && this.olMap.render();
  }

  reset() {
    this.restart();
    this.actionType = 'up';
    this.resetStyle();
    this.clearDelayTimer();
  }

  enable() {
    this.usable = true;
    this.render();
  }

  disable() {
    this.usable = false;
  }

  /** 清除动画 */
  clearAnimation() {
    this.reset();
    this.delayExecuter();
    this.listenerKey && unByKey(this.listenerKey);
  }
}
